import { Injectable } from '@angular/core';
import { MAX_ADDITIONAL_ESCROW, MAX_ADDITIONAL_PRINCIPAL, PAYMENT_HOLIDAYS } from '@ld.consumer-portal/constants';
import { PaymentLoanStatus } from './payment-eligibility.service';
import { getTodayInPST, isTimeBefore6pmPST } from './autopay-utils';

@Injectable({
  providedIn: 'root',
})
export class PaymentDateService {
  private readonly holidays: Date[];

  constructor() {
    this.holidays = PAYMENT_HOLIDAYS.map((dateStr) => {
      const [month, day, year] = dateStr.split('/').map(Number);
      return new Date(year, month - 1, day);
    });
  }

  async ensureHolidaysLoaded(): Promise<void> {
    // Holidays are now loaded synchronously from constants
  }

  getHolidays(): Date[] {
    return this.holidays;
  }

  get maxPrincipal(): number {
    return MAX_ADDITIONAL_PRINCIPAL;
  }

  get maxEscrow(): number {
    return MAX_ADDITIONAL_ESCROW;
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  // Parses an ISO date-only string (e.g. '2026-02-14' from the payment-history
  // API) as a local calendar date; new Date() would shift it to the previous
  // day in US time zones.
  toLocalDate(value: Date | string): Date {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)
      ? new Date(+value.slice(0, 4), +value.slice(5, 7) - 1, +value.slice(8, 10))
      : new Date(value);
  }

  // MSP batches payments dated Saturday, Sunday, or Monday into Monday's
  // end-of-day processing cycle; two dates share a cycle when both are window
  // days anchored to the same Saturday.
  sameMspProcessingWindow(date1: Date | string, date2: Date | string): boolean {
    const isWindowDay = (day: number): boolean => day === 0 || day === 1 || day === 6;
    const day1 = this.toLocalDate(date1);
    const day2 = this.toLocalDate(date2);

    if (
      Number.isNaN(day1.getTime()) ||
      Number.isNaN(day2.getTime()) ||
      this.isSameDay(day1, day2) ||
      !isWindowDay(day1.getDay()) ||
      !isWindowDay(day2.getDay())
    ) {
      return false;
    }

    const toSaturday = (d: Date): number => {
      const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      copy.setDate(copy.getDate() - (copy.getDay() === 0 ? 1 : copy.getDay() === 1 ? 2 : 0));
      return copy.getTime();
    };

    return toSaturday(day1) === toSaturday(day2);
  }

  isWeekend(date: Date): boolean {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  }

  isHoliday(date: Date): boolean {
    return this.holidays.some((holiday) => this.isSameDay(date, holiday));
  }

  isValidBusinessDay(date: Date): boolean {
    return !this.isWeekend(date) && !this.isHoliday(date);
  }

  isValidPaymentDate(date: Date): boolean {
    return !this.isHoliday(date);
  }

  getNextValidPaymentDate(date: Date, maxDate?: Date): Date {
    const result = new Date(date);

    while (!this.isValidPaymentDate(result)) {
      result.setDate(result.getDate() + 1);

      if (maxDate && result > maxDate) {
        return new Date(date);
      }
    }

    return result;
  }

  getNextValidBusinessDay(date: Date, maxDate?: Date): Date {
    const result = new Date(date);

    while (!this.isValidBusinessDay(result)) {
      result.setDate(result.getDate() + 1);

      if (maxDate && result > maxDate) {
        return new Date(date);
      }
    }

    return result;
  }

  getLastBusinessDayBefore(date: Date): Date {
    const result = new Date(date);
    result.setDate(result.getDate() - 1);
    while (!this.isValidBusinessDay(result)) {
      result.setDate(result.getDate() - 1);
    }
    return result;
  }

  getLastBusinessDayOfMonth(date: Date): Date {
    const y = date.getFullYear();
    const m = date.getMonth();
    const lastDay = new Date(y, m + 1, 0);
    while (!this.isValidBusinessDay(lastDay)) {
      lastDay.setDate(lastDay.getDate() - 1);
    }
    return lastDay;
  }

  isSelectedDateOnOrAfterLastBusinessDayOfMonth(selectedDate: Date): boolean {
    const lastBizDay = this.getLastBusinessDayOfMonth(new Date());
    lastBizDay.setHours(0, 0, 0, 0);
    const sel = new Date(selectedDate);
    sel.setHours(0, 0, 0, 0);
    return sel.getTime() >= lastBizDay.getTime();
  }

  isLastWorkingDayPriorToDueDate(dueDate: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const lastBizDay = this.getLastBusinessDayBefore(dueDate);
    lastBizDay.setHours(0, 0, 0, 0);
    return this.isSameDay(today, lastBizDay);
  }

  createDateFilter(minDate: Date, maxDate: Date): (date: Date | null) => boolean {
    return (date: Date | null): boolean => {
      if (!date) return false;

      if (date < minDate || date > maxDate) return false;

      return this.isValidPaymentDate(date);
    };
  }

  getMinOtpPaymentDate(
    loanStatus: PaymentLoanStatus,
    dates: {
      minOneTimePayOnDate: string | null;
      minPayOnDate: string;
      maxOneTimePayOnDate: string | null;
      maxPayOnDate: string;
    },
  ): Date {
    const maxDate = new Date(dates.maxOneTimePayOnDate ?? dates.maxPayOnDate);
    let baseMinDate: Date;

    switch (loanStatus) {
      case PaymentLoanStatus.OnTime:
      case PaymentLoanStatus.OneMonthPaidOff:
      case PaymentLoanStatus.TwoMonthsPaidOff: {
        const today = getTodayInPST();
        if (isTimeBefore6pmPST()) {
          baseMinDate = today;
        } else {
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          baseMinDate = tomorrow;
        }
        break;
      }
      default:
        baseMinDate = new Date(dates.minOneTimePayOnDate ?? dates.minPayOnDate);
        break;
    }

    return this.getNextValidPaymentDate(baseMinDate, maxDate);
  }
}
